import clsx from 'clsx';
import BubbleComponent from 'core/modules/atoms/Bubble';
import { Icon } from 'ui';
import type { IconName } from 'ui/icons/types';
import Tooltip from 'ui/atoms/Tooltip/Tooltip';
import React from 'react';
import { Link } from 'react-router-dom';
import useHeaderTabs, { type Tab } from './useHeaderTabs';

const Bubble = BubbleComponent as unknown as React.ComponentType<Record<string, unknown>>;

type HeaderTabsProps = {
  tabs?: Tab[];
  tabActive?: string;
  onChangeTab?: (id: string) => void;
  onDisableTab?: (id: string) => void;
  /**
   * Variante visual del tablist.
   * @deprecated 'border' y 'pill' — migrar a SimpleTab cuando esté disponible.
   */
  typeView?: 'default' | 'border' | 'background' | 'background-round' | 'vertical' | 'pill';
  align?: 'default' | 'left' | 'right';
  isURLBased?: boolean;
  label?: string;
};

type HeaderTabItemProps = {
  handleKeyDown: (event: React.KeyboardEvent<HTMLElement>, index: number) => void;
  index: number;
  isActive: boolean;
  onClickChangeTab: (tab: Tab) => void;
  registerTabRef: (index: number) => (el: HTMLElement | null) => void;
  tab: Tab;
};

export function tabElementId(id: string): string {
  return `tab-${id}`;
}

export function panelElementId(id: string): string {
  return `panel-${id}`;
}

function HeaderTabItem({ handleKeyDown, index, isActive, onClickChangeTab, registerTabRef, tab }: HeaderTabItemProps): JSX.Element {
  const className = clsx('header-tabs__tab', `header-tabs__tab--id-${tab.id}`, {
    'header-tabs__tab--active': isActive,
    'header-tabs__tab--with-icon': tab.icon,
    'header-tabs__tab--disabled': tab.disabled,
  });

  const sharedAriaProps = {
    role: 'tab' as const,
    id: tabElementId(tab.id),
    'aria-selected': isActive,
    'aria-controls': panelElementId(tab.id),
    'aria-disabled': tab.disabled ? true : undefined,
    tabIndex: tab.disabled ? -1 : 0,
  };

  function handleClick(): void {
    onClickChangeTab(tab);
  }

  function handleKey(event: React.KeyboardEvent<HTMLElement>): void {
    handleKeyDown(event, index);
  }

  const content = (
    <div className="header-tabs__tab-text-container">
      {tab.bubble && (
        <div className="header-tabs__bubble">
          <Bubble />
        </div>
      )}
      {tab.icon && (
        <div className="header-tabs__tab-icon">
          <Icon name={tab.icon as IconName} color={isActive ? 'primary' : 'default'} />
        </div>
      )}
      <span className="header-tabs__tab-text">{tab.name}</span>
    </div>
  );

  const tabElement = tab.to ? (
    <Link
      to={tab.to}
      ref={registerTabRef(index)}
      className={className}
      onClick={handleClick}
      onKeyDown={handleKey}
      {...sharedAriaProps}
    >
      {content}
    </Link>
  ) : (
    <button
      type="button"
      ref={registerTabRef(index)}
      className={className}
      onClick={handleClick}
      onKeyDown={handleKey}
      {...sharedAriaProps}
    >
      {content}
    </button>
  );

  if (tab.tooltip) {
    return (
      <Tooltip placement="right" title={tab.tooltip}>
        {tabElement}
      </Tooltip>
    );
  }

  return tabElement;
}

function HeaderTabs({
  tabs = [],
  tabActive,
  onChangeTab,
  onDisableTab = () => {},
  typeView = 'default',
  align = 'default',
  isURLBased = false,
  label,
}: HeaderTabsProps): JSX.Element {
  const { activeTabState, registerTabRef, onClickChangeTab, handleKeyDown } = useHeaderTabs({
    tabs,
    tabActive,
    onChangeTab,
    onDisableTab,
    isURLBased,
  });

  return (
    <div className={clsx('header-tabs', `header-tabs__type-view-${typeView}`, `header-tabs__align-${align}`)}>
      <div role="tablist" aria-orientation={typeView === 'vertical' ? 'vertical' : 'horizontal'} aria-label={label}>
        {tabs.map((tab, index) => (
          <HeaderTabItem
            key={tab.id}
            handleKeyDown={handleKeyDown}
            index={index}
            isActive={tab.id === activeTabState}
            onClickChangeTab={onClickChangeTab}
            registerTabRef={registerTabRef}
            tab={tab}
          />
        ))}
      </div>
    </div>
  );
}

export default HeaderTabs;
